import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@cardboard-ui/react';
import { plural, t } from '@lingui/macro';
import { Textarea } from 'components/Form/Textarea';
import { OverlayProps } from 'components/TaskOverlay';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { graphql, useMutation } from 'utils/graphClient';
import { authenticatedHttpRequest } from 'utils/http';
import { useFileUploader } from 'utils/upload';
import { ConfirmSignatureModal_createSignatureEnvelopeMutation } from './__generated__/ConfirmSignatureModal_createSignatureEnvelopeMutation.graphql';
import { postMessageWTC } from '../../utils';

const createSignatureEnvelopeMutation = graphql`
  mutation ConfirmSignatureModal_createSignatureEnvelopeMutation(
    $nodeId: ID!
    $signatures: [SignatureInput!]!
    $fileToSign: FileInput!
    $message: String
  ) {
    createSignatureEnvelope(
      input: {
        nodeId: $nodeId
        message: $message
        signatures: $signatures
        fileToSign: $fileToSign
      }
    ) {
      node {
        id
        ...ItemViewer_node
      }
    }
  }
`;

interface ConfirmSignatureModalProps extends OverlayProps {
  nodeId: string;
  appliedMemberIds: string[];
  onComplete: () => void;
  currentFileUrl: string;
}

export const ConfirmSignatureModal: React.FC<ConfirmSignatureModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  nodeId,
  appliedMemberIds,
  currentFileUrl,
}) => {
  const { register, getValues } = useForm();
  const toast = useToast();
  const uniqueAppliedMemberIds = uniq(appliedMemberIds);
  const { uploadFile, completedFiles } = useFileUploader('FILES', {
    maxNumberOfFiles: 1,
    minNumberOfFiles: 1,
    allowedFileTypes: ['application/pdf'],
  });

  const [signeeIds, setSigneeIds] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const [createSignatureRequest] =
    useMutation<ConfirmSignatureModal_createSignatureEnvelopeMutation>(
      createSignatureEnvelopeMutation,
    );

  const saveSignatureRequest = useCallback(
    ({
      certifiedUploadToken,
      signeeIds,
    }: {
      signeeIds: string[];
      certifiedUploadToken: string;
    }) => {
      createSignatureRequest({
        variables: {
          nodeId,
          signatures: signeeIds.map((signeeId) => {
            return {
              id: signeeId,
              memberId: signeeId.replace('sig-', ''),
            };
          }),
          fileToSign: { uploadToken: certifiedUploadToken },
          //signaturesXfdf: signingTemplateXfdf,
          message: getValues('message'),
        },
        onCompleted: () => {
          setIsLoading(false);
          onComplete();
        },
        onError: () => {
          setIsLoading(false);
          toast({
            title: t`Couldn't create your signature request`,
            status: 'error',
            isClosable: true,
          });
        },
      });
    },
    [createSignatureRequest, toast, nodeId],
  );

  const observeWebViewer = useCallback(
    (event: any) => {
      if (event.data.type === 'crdbrd-ctow-signableFileBuilt') {
        const { docBuffer, docFilename, signeeIds } = event.data;

        setSigneeIds(signeeIds);

        const file = new File([docBuffer], docFilename, {
          type: 'application/pdf',
        });

        uploadFile(file);
      }
    },
    [saveSignatureRequest],
  );

  useEffect(() => {
    if (completedFiles.length > 0 && signeeIds.length > 0) {
      saveSignatureRequest({
        signeeIds: signeeIds,
        certifiedUploadToken: completedFiles[0].meta.uploadToken || '',
      });

      onClose();
    }
  }, [completedFiles.length, signeeIds]);

  useEffect(() => {
    window.addEventListener('message', observeWebViewer);
    return () => window.removeEventListener('message', observeWebViewer);
  }, [observeWebViewer]);

  const signatureRequestCount = uniqueAppliedMemberIds.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
    >
      <ModalOverlay />
      <ModalContent data-qa="confirm-signature-modal-content">
        <ModalHeader>
          {plural(signatureRequestCount, {
            one: `Confirm ${signatureRequestCount} signature request`,
            other: `Confirm ${signatureRequestCount} signature requests`,
          })}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6}>
            <Text>{t`Confirming signature request will notify signers via email and in app notification, that their signature is requested.`}</Text>
            <Text>{t`You can follow signature status inside the document, but you also together with other vault managers will get notified when the document is signed.`}</Text>
            <Textarea
              label={t`Add a message`}
              placeholder={t`Your feedback`}
              {...register('message')}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            isDisabled={isLoading}
            isLoading={isLoading}
            onClick={async () => {
              setIsLoading(true);
              const currentFileForCertifying = await fetchCurrentFileUrl(
                currentFileUrl,
              );
              buildSignableFile(currentFileForCertifying);
            }}
          >{t`Request signature`}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const fetchCurrentFileUrl = async (currentFileUrl: string) => {
  const response = await authenticatedHttpRequest(`${currentFileUrl}.json`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
    },
  }).then((result) => result.json());

  return response.url;
};

// Tell the webviewer to finalize the signatures. Make them ready to be saved.
const buildSignableFile = (currentFileUrl: string) => {
  postMessageWTC({
    type: 'buildSignableFile',
    currentFileUrl,
  });
};
